import React, { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { FaCrown, FaGem } from "react-icons/fa";
import { motion } from "framer-motion";
import { LanguageContext } from "../context/LanguageContext";
import useTranslation from "../hooks/useTranslation";
import LanguageSelector from "../components/LanguageSelector";

const Register = () => {
  const { t } = useTranslation();
  const { language } = useContext(LanguageContext);
  const navigate = useNavigate();

  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [selectedPlan, setSelectedPlan] = useState("free");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess(false);
    setLoading(true);

    if(!acceptedTerms) {
      setSuccess(false);
      setError("You must agree with our terms & conditions.");
      setLoading(false);
      return;
    }

    const isPasswordValid =
      passwordRules.minLength &&
      passwordRules.uppercase &&
      passwordRules.lowercase &&
      passwordRules.number &&
      passwordRules.special;

    if (!isPasswordValid) {
      setError(
        "Password must contain uppercase, lowercase, number, special character and be at least 8 characters long."
      );
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/user/register`,
        {
          username,
          email,
          password,
          plan: selectedPlan,
          language,
        },
      );

      if (response.status === 201) {
        setSuccess(true);
        setTimeout(() => navigate("/login"), 2000);
      }
      console.log('inside:', response);
    } catch (err) {
      console.log('outside', err.response.data.error);
      setError(err.response?.data?.error || t("registrationFailed"));
    } finally {
      setLoading(false);
    }
  };

  const passwordRules = {
    minLength: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /\d/.test(password),
    special: /[@$!%*?&.#_\-]/.test(password),
  };

  // auto-hide error după 3 secunde
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const subscriptionPlans = [
    {
      id: "basic",
      name: t("Basic"),
      price: "€4.99",
      features: [
        t("Calendar access"), 
        t("Unlimited Projects"),
        t("Today's matches"),
        t("Basic Event Filters"),
        t("Basic Statistics"),
        t("Standard support")
      ],
      icon: <FaGem className="text-blue-400 text-lg" />,
    },
    {
      id: "pro",
      name: t("Pro"),
      price: "€13.99",
      features: [
        t("Everything in Basic"),
        t("Daily Value Snapshot"),
        t("AI Predictions"),
        t("Advanced statistics"),
        t("Value event analysis"),
        t("Priority Support")
      ],
      icon: <FaGem className="text-green-400 text-lg" />,
    },
    {
      id: "premium",
      name: t("Premium"),
      price: "€19.99",
      features: [
        t("Everything in Pro"),
        t("Premium Analysis"),
        t("Advanced Premium AI models"),
        t("Exclusive insights"),
        t("Unlimited daily AI-analyzed matches"),
        t("VIP support")
      ],
      icon: <FaCrown className="text-yellow-400 text-lg" />,
    },
  ];

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-black via-gray-900 to-black p-4 relative">
      <motion.div
        className="relative bg-gray-900/80 border border-green-500/30 rounded-2xl shadow-lg shadow-green-500/30 p-8 max-w-2xl w-full backdrop-blur-xl"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        {/* Glow exterior */}
        <div className="absolute inset-0 rounded-2xl border border-green-500/20 shadow-[0_0_40px_rgba(34,197,94,0.4)] pointer-events-none"></div>

        {/* Language selector */}
        <div className="absolute -top-3 -right-3 z-20">
          <LanguageSelector />
        </div>

        {/* Titlu */}
        <div className="pt-6 text-center">
          <h1 className="text-3xl font-bold text-green-400 mb-2 text-center tracking-wide">
            {t("createYourAccount")}
          </h1>
          {/* <motion.h1
            className="text-3xl font-bold text-green-300 mb-2 text-center tracking-wide"
            initial={{ opacity: 1, textShadow: "0 0 8px rgba(0,255,150,0.7)" }}
            animate={{
              opacity: [1, 0.8, 1, 0.6, 1, 0.9, 1],
              textShadow: [
                "0 0 8px rgba(0,255,150,0.7)",
                "0 0 20px rgba(0,255,150,1)",
                "0 0 12px rgba(0,255,150,0.8)",
                "0 0 30px rgba(0,255,150,0.9)",
                "0 0 10px rgba(0,255,150,0.6)",
              ],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          >
            {t("createYourAccount")}
          </motion.h1> */}
          <p className="text-gray-400 mb-6">{t("startManaging")}</p>
        </div>

        {/* Error popup animat */}
        {error && (
          <motion.div
            className="absolute inset-0 flex items-center justify-center bg-black/70 z-30"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="bg-red-600/90 p-4 rounded-lg shadow-lg max-w-sm w-full mx-4">
              <p className="font-bold text-white text-center">{t("error")}</p>
              <p className="text-gray-200 text-center">{error}</p>
            </div>
          </motion.div>
        )}

        {/* Success & loading messages (non-popup) */}
        {success && (
          <div className="mb-6 p-3 bg-green-600/90 text-white rounded-lg text-center">
            {t("accountCreated")}
          </div>
        )}
        {loading && (
          <div className="mb-6 p-3 bg-blue-600/90 text-white rounded-lg text-center animate-pulse">
            {t("creatingAccount")}
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Account form */}
          <motion.div
            className="lg:w-1/2 w-full"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1, duration: 0.8 }}
          >
            <h2 className="text-xl font-bold text-white mb-4 text-center lg:text-left">
              {t("accountDetails")}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  {t("username")}
                </label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-800/60 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-green-400 focus:border-green-400 transition"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  {t("email")}
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-800/60 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-green-400 focus:border-green-400 transition"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  {t("password")}
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-800/60 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-green-400 focus:border-green-400 transition"
                  required
                />
              </div>

              <div className="mt-2 text-sm space-y-1">
                <p className={passwordRules.minLength ? "text-green-400" : "text-red-400"}>
                  • Minimum 8 characters
                </p>

                <p className={passwordRules.uppercase ? "text-green-400" : "text-red-400"}>
                  • One uppercase letter
                </p>

                <p className={passwordRules.lowercase ? "text-green-400" : "text-red-400"}>
                  • One lowercase letter
                </p>

                <p className={passwordRules.number ? "text-green-400" : "text-red-400"}>
                  • One number
                </p>

                <p className={passwordRules.special ? "text-green-400" : "text-red-400"}>
                  • One special character
                </p>
              </div>

              {/* Terms Checkbox */}
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="terms"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-gray-600 bg-gray-800 text-green-500 focus:ring-green-400"
                  required
                />

                <label
                  htmlFor="terms"
                  className="text-sm text-gray-300 leading-6"
                >
                  I accept the{" "}
                  <a
                    href="/terms-of-use"
                    className="text-green-400 hover:text-green-300 underline underline-offset-2 transition"
                  >
                    Terms & Conditions
                  </a>{" "}
                  and{" "}
                  <a
                    href="/privacy"
                    className="text-green-400 hover:text-green-300 underline underline-offset-2 transition"
                  >
                    Privacy Policy
                  </a>
                </label>
              </div>

              <input type="hidden" value={selectedPlan} />
              <button
                type="submit"
                disabled={!acceptedTerms}
                className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white font-bold py-3 px-4 rounded-lg shadow-[0_0_20px_rgba(34,197,94,0.6)] hover:from-green-600 hover:to-green-700 transition-all duration-200 mt-4"
                className={`w-full text-white font-bold py-3 px-4 rounded-lg transition-all duration-200 mt-4
                ${
                  acceptedTerms
                    ? "bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 shadow-[0_0_20px_rgba(34,197,94,0.6)]"
                    : "bg-gray-700 cursor-not-allowed opacity-60"
                }`}
              >
                {t("createAccountBtn")}
              </button>
            </form>
            <p className="text-gray-400 text-sm mt-6 text-center">
              {t("alreadyHaveAccount")}{" "}
              <a
                href="/login"
                className="text-green-400 hover:text-green-300 underline"
              >
                {t("signInLink")}
              </a>
            </p>
            <motion.div 
                key="free"
                onClick={() => setSelectedPlan("free")}
                className={`relative mt-10 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    selectedPlan === "free"
                      ? "border-yellow-400 bg-gray-800/60 shadow-[0_0_20px_rgba(34,197,94,0.4)]"
                      : "border-gray-700 hover:border-gray-600"
                  }`}>
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                Free Trial
              </h3>
            </motion.div>
          </motion.div>

          {/* Subscription plans */}
          <motion.div
            className="lg:w-1/2 w-full"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            <h2 className="text-xl font-bold text-white mb-4 text-center lg:text-left">
              {t("chooseYourPlan")}
            </h2>
            <div className="space-y-4">
              {subscriptionPlans.map((plan) => (
                <motion.div
                  key={plan.id}
                  onClick={() => setSelectedPlan(plan.id)}
                  className={`relative p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    selectedPlan === plan.id
                      ? "border-green-400 bg-gray-800/60 shadow-[0_0_20px_rgba(34,197,94,0.4)]"
                      : "border-gray-700 hover:border-gray-600"
                  }`}
                  whileHover={{ scale: 1.03 }}
                >
                  <div className="flex justify-between items-start">
                    <h3 className="text-lg font-bold text-white flex items-center gap-2">
                      {plan.icon}
                      {plan.name}
                    </h3>
                    <div
                      className={`absolute top-4 right-4 ${
                        selectedPlan === plan.id
                          ? "bg-green-900/30 border border-green-400"
                          : "bg-black/20"
                      } rounded-full px-3 py-1`}
                    >
                      <p className="text-lg font-bold text-white">
                        {plan.price}
                      </p>
                    </div>
                  </div>
                  <ul className="mt-4 space-y-1">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="text-gray-300 text-sm">
                        ✓ {feature}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default Register;
